<template>
  <div>
    <ul class="breadcrumb">
      <li v-if="!$route.meta.noProjectOk">
        <router-link :to="{ name: 'collections-home' }">
          <i class="glyphicon glyphicon-home" /> Collections
        </router-link>
      </li>

      <li
        v-if="
          ($route.meta.noProjectOk && $route.params.orgId) ||
          (!$route.meta.noProjectOk && project && project.org_id)
        "
        class="dropdown"
      >
        <a id="organizationsLabel" class="dropdown-toggle" data-toggle="dropdown" href="#">
          <i class="fa fa-users" />
          Collection:
          <b class="caret" />
        </a>
        <ul class="dropdown-menu" role="menu" aria-labelledby="organizationsLabel">
          <li v-for="org in sortedOrganizations" :key="'bread-org-' + org.id">
            <router-link
              v-if="org.id"
              :to="{ name: 'organization-home', params: { orgId: org.id } }"
            >
              <i class="fa fa-users" />
              {{ org.name }}
            </router-link>
          </li>
        </ul>

        <span v-if="!$route.meta.noProjectOk && project && project.org_id" style="margin-left: 6px">
          <router-link
            v-if="project.org_id"
            :to="{ name: 'organization-home', params: { orgId: project.org_id } }"
          >
            {{ projectOrgName(project.org_id) }}
          </router-link>
        </span>
      </li>

      <template v-if="!$route.meta.noProjectOk">
        <li class="dropdown">
          <a id="projectsLabel" class="dropdown-toggle" data-toggle="dropdown" href="#">
            <i class="fa fa-folder" />
            Project:
            <b class="caret" />
          </a>
          <ul class="dropdown-menu" role="menu" aria-labelledby="projectsLabel">
            <li v-for="proj in sortedProjects" :key="'bread-proj-' + proj.id">
              <a
                @click.prevent="goToProject(proj.project.id)"
                @keyup.enter="goToProject(proj.project.id)"
              >
                <UserPlusIcon
                  v-if="proj.access_level === 'OWNER'"
                  v-tooltip="'Project owner'"
                  size="10"
                />
                <UserCheckIcon
                  v-else-if="proj.access_level === 'EDITOR'"
                  v-tooltip="'Project editor'"
                  size="10"
                />
                <UserIcon
                  v-else-if="proj.access_level === 'ANALYST'"
                  v-tooltip="'Project analyst'"
                  size="10"
                />
                <UserMinusIcon
                  v-else-if="proj.access_level === 'VIEWER'"
                  v-tooltip="'Project viewer'"
                  size="10"
                />
                <UserXIcon
                  v-else-if="proj.access_level === 'NONE'"
                  v-tooltip="'No view access'"
                  size="10"
                />
                {{ proj.project.name }}
              </a>
            </li>
          </ul>

          <span v-if="projectId && !$route.meta.noProjectOk" style="margin-left: 6px">
            <router-link :to="{ name: 'project-home' }">
              {{ project ? project.name : 'Project' }}
            </router-link>
          </span>
        </li>
      </template>

      <template v-if="crumbs.length">
        <li
          v-for="(crumb, idx) in crumbs"
          :key="'breadcrumb-' + idx"
          :class="crumb.dropdown ? 'dropdown' : ''"
        >
          <a
            v-if="crumb.dropdown"
            :id="'breadcrumb' + crumb.dropdown + 'Label'"
            class="dropdown-toggle"
            data-toggle="dropdown"
            href="#"
            style="margin-right: 6px"
          >
            <i :class="crumb.icon || 'glyphicon glyphicon-question-sign'" />
            {{ crumb.title }}
            <b class="caret" />
          </a>
          <ul
            v-if="crumb.dropdown"
            class="dropdown-menu"
            role="menu"
            :aria-labelledby="'breadcrumb' + crumb.dropdown + 'Label'"
          >
            <li
              v-for="{ id, text, route } in getCrumbList(crumb.dropdown)"
              :key="'bread-' + crumb.dropdown + id"
            >
              <router-link :to="route">
                {{ text }}
              </router-link>
            </li>
          </ul>

          <router-link v-else-if="crumb.to.name" :to="crumb.to">
            <i :class="crumb.icon || 'glyphicon glyphicon-question-sign'" />
            {{ crumb.title }}
          </router-link>
          <a v-else-if="crumb.to.nonVue" :href="getNonVue(crumb.to)">
            <i :class="crumb.icon || 'glyphicon glyphicon-question-sign'" />
            {{ crumb.title }}
          </a>
          <a v-else-if="crumb.title" href="#">
            <i :class="crumb.icon || 'glyphicon glyphicon-question-sign'" />
            {{ crumb.title }}
          </a>
        </li>
        <li v-if="$route.meta.links" class="dropdown">
          <a id="dLabel" class="dropdown-toggle" data-toggle="dropdown" href="#"
            >Go to... <b class="caret"
          /></a>
          <ul class="dropdown-menu" role="menu" aria-labelledby="dLabel">
            <template v-for="(link, idx) in $route.meta.links">
              <li v-if="link.to" :key="'link-' + idx">
                <router-link :to="{ name: link.to, params: link.params }">
                  {{ link.title }}
                </router-link>
              </li>
              <li v-else-if="link.nonVue" :key="'link-nv-' + idx">
                <a :href="getNonVue(link)">
                  {{ link.title }}
                </a>
              </li>
              <li v-else :key="'link-' + idx" class="divider" />
            </template>
          </ul>
        </li>
      </template>
    </ul>
  </div>
</template>

<script>
import { get, call } from 'vuex-pathify';

import { UserPlusIcon, UserMinusIcon, UserCheckIcon, UserIcon, UserXIcon } from 'vue-feather-icons';

export default {
  name: 'BreadcrumbsList',
  components: {
    UserPlusIcon,
    UserCheckIcon,
    UserMinusIcon,
    UserIcon,
    UserXIcon,
  },
  computed: {
    crumbs() {
      const routes = this.$route.matched
        .map((route) => ({
          to: {
            name: route.name || route.meta.defaultChild,
            nonVue: route.meta.nonVue,
            params: this.$route.params,
          },
          title: this.getBreadcrumb(route),
          icon: route.meta.icon,
          dropdown: route.meta.dropdown,
          breadSkip: route.meta.breadSkip || false,
        }))
        .filter((route) => route.title && !route.breadSkip);
      return routes;
    },
    sortedProjects() {
      const projs = Object.values(this.projects).filter(
        ({ project }) => !this.project || project.org_id === this.project.org_id,
      );
      return projs.sort((a, b) => a.project?.name.localeCompare(b.project?.name));
    },
    sortedOrganizations() {
      const orgs = Object.values(this.organizations);
      return orgs.sort((a, b) => a?.name.localeCompare(b?.name)).filter((o) => o);
    },

    callsList() {
      return this.listCalls
        .map(({ id, meta }) => ({
          id,
          text: meta.name,
          route: {
            to: this.isProjectEditor ? 'calls-edit' : 'calls-preview',
            params: {
              callId: id,
            },
          },
        }))
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    workflowsList() {
      return this.listWorkflows
        .map(({ id, meta }) => ({
          id,
          text: meta.name,
          route: {
            to: this.isProjectEditor ? 'workflows-edit' : 'workflows-preview',
            params: {
              workflowId: id,
            },
          },
        }))
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    isProjectEditor: get('account2/isProjectEditor'),
    getPath: get('nonVueRoutes/getPath'),
    projectId: get('account2/projectId'),
    projects: get('account2/projects'),
    project: get('account2/projects[:projectId].project'),
    organizations: get('account2/organizations'),
    pageTitles: get('account2/pageTitles'),
    listCalls: get('apimAPI/listCalls'),
    listWorkflows: get('apimAPI/listWorkflows'),
  },
  watch: {
    projectId: 'fetchData',
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    fetchData() {
      if (this.projectId) {
        this.requireCalls();
        this.requireWorkflows();
      }
    },

    async goToProject(id) {
      await this.setProject({ id });
      if (this.$route.name !== 'project-home') {
        this.$router.push({ name: 'project-home' });
      }
    },
    getNonVue(link) {
      const { params: variables } = this.$route;
      return this.getPath({ name: link.nonVue, variables });
    },
    projectOrgName(orgId) {
      return this.organizations[orgId]?.name || '?';
    },
    getBreadcrumb(route) {
      const { breadcrumb = null, title, defaultChild } = route.meta;
      const routeName = route.name || defaultChild;
      const shortened = this.pageTitles[routeName]?.breadcrumb || breadcrumb;
      return shortened || title;
    },
    getCrumbList(hint) {
      return {
        calls: this.callsList,
        workflows: this.workflowsList,
      }[hint];
    },

    setProject: call('account2/setProject'),
    requireCalls: call('apimAPI/requireCalls'),
    requireWorkflows: call('apimAPI/requireWorkflows'),
  },
};
</script>

<style scoped>
.container {
  margin: auto;
  width: 50%;
}
</style>
